import { useRef, useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Save,
  Edit,
  X,
  Award,
  Upload,
  Loader2,
  Banknote,
  Image,
  Target,
  Building,
  Smartphone,
  RefreshCw,
  LogOut,
  CheckCircle2,
} from 'lucide-react';
import { TabsContent } from '../ui/tabs';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';

const SettingsTab = ({
  settings,
  setSettings,
  editMode,
  setEditMode,
  uploadingSeal,
  sealFileRef,
  updateSettings,
  uploadSealImage,
  deleteSealImage,
  getImageUrl,
  API,
}) => {
  const logoFileRef = useRef(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const { t } = useLanguage();

  const [waStatus, setWaStatus] = useState(null);
  const [waLoading, setWaLoading] = useState(false);

  const fetchWaStatus = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${API}/admin/whatsapp/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWaStatus(res.data);
    } catch (err) {
      setWaStatus({ connected: false, qr: null, error: 'unreachable' });
    }
  };

  const logoutWa = async () => {
    if (!window.confirm('WhatsApp連携を解除します。再度QRスキャンが必要になります。よろしいですか？')) return;
    setWaLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/admin/whatsapp/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTimeout(fetchWaStatus, 1500);
    } catch (err) {
      console.error('WA logout failed:', err);
    } finally {
      setWaLoading(false);
    }
  };

  useEffect(() => {
    fetchWaStatus();
    const iv = setInterval(fetchWaStatus, 6000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadLogoImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(`${API}/admin/settings/logo`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.logoImage) {
        setSettings(prev => ({ ...prev, logoImage: res.data.logoImage }));
      }
    } catch (err) {
      console.error('Logo upload failed:', err);
    } finally {
      setUploadingLogo(false);
      if (logoFileRef.current) logoFileRef.current.value = '';
    }
  };

  const deleteLogoImage = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/settings/logo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({ ...prev, logoImage: null }));
    } catch (err) {
      console.error('Logo delete failed:', err);
    }
  };

  return (
    <TabsContent value="settings" className="p-6">
      {settings && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">{t('admin.settingsTab.title')}</h2>
            {editMode ? (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEditMode(false)}>
                  <X className="h-4 w-4 mr-2" />{t('admin.settingsTab.cancel')}
                </Button>
                <Button className="bg-[#00A651] hover:bg-[#008a43]" onClick={updateSettings}>
                  <Save className="h-4 w-4 mr-2" />{t('admin.settingsTab.save')}
                </Button>
              </div>
            ) : (
              <Button onClick={() => setEditMode(true)}>
                <Edit className="h-4 w-4 mr-2" />{t('admin.settingsTab.edit')}
              </Button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label>{t('admin.settingsTab.orgNameJa')}</Label>
              <Input value={settings.organizationName || ''} onChange={(e) => setSettings({...settings, organizationName: e.target.value})} disabled={!editMode} className="mt-1" />
            </div>
            <div>
              <Label>{t('admin.settingsTab.orgNameEn')}</Label>
              <Input value={settings.organizationNameEn || ''} onChange={(e) => setSettings({...settings, organizationNameEn: e.target.value})} disabled={!editMode} className="mt-1" />
            </div>
            <div>
              <Label>代表者名（役職＋氏名）</Label>
              <Input value={settings.representativeName || ''} onChange={(e) => setSettings({...settings, representativeName: e.target.value})} disabled={!editMode} className="mt-1" placeholder="例：代表理事 山田太郎" data-testid="settings-representative-name" />
              <p className="text-xs text-gray-500 mt-1">請求書・領収書・見積書PDFの団体名の下に表示されます（空欄なら表示しません）</p>
            </div>
            <div>
              <Label>{t('admin.settingsTab.catchphrase')}</Label>
              <Input value={settings.catchphrase || ''} onChange={(e) => setSettings({...settings, catchphrase: e.target.value})} disabled={!editMode} className="mt-1" />
            </div>
            <div>
              <Label>{t('admin.settingsTab.phone')}</Label>
              <Input value={settings.phone || ''} onChange={(e) => setSettings({...settings, phone: e.target.value})} disabled={!editMode} className="mt-1" />
            </div>
            <div>
              <Label>{t('admin.settingsTab.email')}</Label>
              <Input value={settings.email || ''} onChange={(e) => setSettings({...settings, email: e.target.value})} disabled={!editMode} className="mt-1" />
            </div>
            <div>
              <Label>{t('admin.settingsTab.businessHours')}</Label>
              <Input value={settings.businessHours || ''} onChange={(e) => setSettings({...settings, businessHours: e.target.value})} disabled={!editMode} className="mt-1" />
            </div>
            <div>
              <Label>{t('admin.settingsTab.whatsapp')}</Label>
              <Input value={settings.whatsapp || ''} onChange={(e) => setSettings({...settings, whatsapp: e.target.value})} disabled={!editMode} placeholder="例: +81-90-1234-5678" className="mt-1" />
            </div>
            <div>
              <Label>WhatsApp 通知先番号</Label>
              <Textarea value={settings.whatsappNotifyNumbers || ''} onChange={(e) => setSettings({...settings, whatsappNotifyNumbers: e.target.value})} disabled={!editMode} placeholder="1行に1番号（国番号付き）&#10;例:&#10;+819012345678&#10;+818012345678" rows={4} className="mt-1" data-testid="settings-wa-numbers" />
              <p className="text-[11px] text-gray-400 mt-1">新規申込時にこれらの番号へWhatsApp自動通知を送信します（1行に1番号・国番号付き）</p>
              <label className="mt-2 flex items-center justify-between gap-2 rounded-md border bg-gray-50 px-3 py-2 text-sm">
                <span>
                  未対応申込のWhatsAppリマインダー
                  <span className="block text-[11px] text-gray-400">未対応の申込がある間、{({ 5: '5分', 15: '15分', 30: '30分', 60: '1時間', 120: '2時間', 240: '4時間' })[settings.whatsappReminderIntervalMin || 5] || `${settings.whatsappReminderIntervalMin}分`}ごとに上の番号へまとめて通知します</span>
                </span>
                <Switch
                  checked={settings.whatsappPendingReminderEnabled !== false}
                  onCheckedChange={(v) => setSettings({ ...settings, whatsappPendingReminderEnabled: v })}
                  disabled={!editMode}
                  data-testid="settings-wa-reminder-switch"
                />
              </label>
              {settings.whatsappPendingReminderEnabled !== false && (
                <div className="mt-2 rounded-md border bg-gray-50 px-3 py-2 space-y-2 text-sm" data-testid="settings-wa-reminder-options">
                  <div className="flex items-center justify-between gap-2">
                    <span>通知の間隔</span>
                    <select
                      value={settings.whatsappReminderIntervalMin || 5}
                      onChange={(e) => setSettings({ ...settings, whatsappReminderIntervalMin: Number(e.target.value) })}
                      disabled={!editMode}
                      className="border rounded-md px-2 py-1 text-sm bg-white"
                      data-testid="settings-wa-reminder-interval"
                    >
                      {[[5, '5分ごと'], [15, '15分ごと'], [30, '30分ごと'], [60, '1時間ごと'], [120, '2時間ごと'], [240, '4時間ごと']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span>営業時間内だけ送る（日本時間）</span>
                    <Switch
                      checked={!!settings.whatsappReminderQuietEnabled}
                      onCheckedChange={(v) => setSettings({ ...settings, whatsappReminderQuietEnabled: v })}
                      disabled={!editMode}
                      data-testid="settings-wa-reminder-quiet-switch"
                    />
                  </div>
                  {settings.whatsappReminderQuietEnabled && (
                    <div className="flex flex-wrap items-center gap-2">
                      <Input type="time" value={settings.whatsappReminderStart || '09:00'} onChange={(e) => setSettings({ ...settings, whatsappReminderStart: e.target.value })} disabled={!editMode} className="w-[120px] h-8" data-testid="settings-wa-reminder-start" />
                      <span>〜</span>
                      <Input type="time" value={settings.whatsappReminderEnd || '18:00'} onChange={(e) => setSettings({ ...settings, whatsappReminderEnd: e.target.value })} disabled={!editMode} className="w-[120px] h-8" data-testid="settings-wa-reminder-end" />
                      <label className="flex items-center gap-1.5 ml-2">
                        <input type="checkbox" checked={settings.whatsappReminderSkipWeekends !== false} onChange={(e) => setSettings({ ...settings, whatsappReminderSkipWeekends: e.target.checked })} disabled={!editMode} data-testid="settings-wa-reminder-skip-weekends" />
                        土日は送らない
                      </label>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div>
              <Label>WhatsApp 決済通知先番号</Label>
              <Textarea value={settings.whatsappPaymentNotifyNumbers || ''} onChange={(e) => setSettings({...settings, whatsappPaymentNotifyNumbers: e.target.value})} disabled={!editMode} placeholder="1行に1番号（国番号付き）&#10;例:&#10;+819039387570" rows={4} className="mt-1" data-testid="settings-wa-payment-numbers" />
              <p className="text-[11px] text-gray-400 mt-1">クレジットカード決済が完了した時にこれらの番号へWhatsApp自動通知を送信します（未入力の場合は上の申込通知先を使用）</p>
            </div>
            <div className="md:col-span-2">
              <Label className="flex items-center gap-2"><Smartphone className="h-4 w-4 text-[#25D366]" /> WhatsApp 連携（自動送信）</Label>
              <div className="mt-2 border rounded-lg p-4 bg-gray-50" data-testid="wa-connection-card">
                {waStatus?.connected ? (
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-2 text-green-700 font-medium" data-testid="wa-status-connected">
                      <CheckCircle2 className="h-5 w-5" /> 接続済み（自動送信が有効です）
                    </div>
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={fetchWaStatus} data-testid="wa-refresh-btn"><RefreshCw className="h-4 w-4 mr-1" />更新</Button>
                      <Button type="button" variant="outline" size="sm" className="text-red-600 border-red-300" onClick={logoutWa} disabled={waLoading} data-testid="wa-logout-btn"><LogOut className="h-4 w-4 mr-1" />連携解除</Button>
                    </div>
                  </div>
                ) : waStatus?.qr ? (
                  <div className="flex flex-col items-center gap-2 text-center">
                    <p className="text-sm text-gray-700 font-medium">スマホのWhatsApp →「設定」→「リンク済みデバイス」→「デバイスをリンク」で下のQRをスキャンしてください</p>
                    <img src={waStatus.qr} alt="WhatsApp QR" className="w-56 h-56" data-testid="wa-qr-image" />
                    <Button type="button" variant="outline" size="sm" onClick={fetchWaStatus} data-testid="wa-refresh-btn"><RefreshCw className="h-4 w-4 mr-1" />QRを更新</Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="text-sm text-gray-500" data-testid="wa-status-waiting">
                      {waStatus?.error ? 'WhatsAppサービスに接続できません（サーバー側サービス未起動の可能性）' : '接続待機中… QRコードを取得しています'}
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={fetchWaStatus} data-testid="wa-refresh-btn"><RefreshCw className="h-4 w-4 mr-1" />更新</Button>
                  </div>
                )}
                <p className="text-[11px] text-gray-400 mt-3">※ ご自身のWhatsAppアカウントを連携して自動送信します（Twilio不要・無料）。連携解除するとQR再スキャンが必要です。</p>
              </div>
            </div>
            <div>
              <Label>{t('admin.settingsTab.lineId')}</Label>
              <Input value={settings.lineId || ''} onChange={(e) => setSettings({...settings, lineId: e.target.value})} disabled={!editMode} placeholder="例: @jwsea" className="mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label>{t('admin.settingsTab.address')}</Label>
              <Input value={settings.address || ''} onChange={(e) => setSettings({...settings, address: e.target.value})} disabled={!editMode} className="mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label>{t('admin.settingsTab.description')}</Label>
              <Textarea value={settings.description || ''} onChange={(e) => setSettings({...settings, description: e.target.value})} disabled={!editMode} rows={3} className="mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label className="flex items-center gap-2">スケジュール設定</Label>
              <label className="mt-2 flex items-center gap-3 p-3 border rounded-lg bg-gray-50 cursor-pointer">
                <input type="checkbox" checked={!!settings.allowPastScheduleDates} disabled={!editMode} onChange={(e) => setSettings({...settings, allowPastScheduleDates: e.target.checked})} className="rounded" data-testid="settings-allow-past-dates" />
                <span className="text-sm"><span className="font-medium">過去日の登録を許可</span><br /><span className="text-xs text-gray-500">オフの場合、スケジュール登録時に過去の日付を登録できません</span></span>
              </label>
            </div>
          </div>

          {/* Organization Profile / 組織概要 */}
          <div className="border-t pt-6 mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Building className="h-5 w-5 text-[#00A651]" />
              {t('admin.settingsTab.orgProfile')}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {t('admin.settingsTab.orgProfileDesc')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>{t('admin.settingsTab.establishedDate')}</Label>
                <Input value={settings.establishedDate || ''} onChange={(e) => setSettings({...settings, establishedDate: e.target.value})} disabled={!editMode} placeholder="例: 2020年4月" className="mt-1" />
              </div>
              <div>
                <Label>{t('admin.settingsTab.representative')}</Label>
                <Input value={settings.representative || ''} onChange={(e) => setSettings({...settings, representative: e.target.value})} disabled={!editMode} placeholder="例: 田中 太郎" className="mt-1" />
              </div>
              <div>
                <Label>{t('admin.settingsTab.employeeCount')}</Label>
                <Input value={settings.employeeCount || ''} onChange={(e) => setSettings({...settings, employeeCount: e.target.value})} disabled={!editMode} placeholder="例: 15名" className="mt-1" />
              </div>
              <div>
                <Label>{t('admin.settingsTab.supportedLanguages')}</Label>
                <Input value={settings.supportedLanguages || ''} onChange={(e) => setSettings({...settings, supportedLanguages: e.target.value})} disabled={!editMode} placeholder="例: 日本語、英語、ポルトガル語" className="mt-1" />
              </div>
              <div className="md:col-span-2">
                <Label>{t('admin.settingsTab.orgProfileExtra')}</Label>
                <Textarea value={settings.orgProfileExtra || ''} onChange={(e) => setSettings({...settings, orgProfileExtra: e.target.value})} disabled={!editMode} rows={3} placeholder={t('admin.settingsTab.orgProfileExtraPlaceholder')} className="mt-1" />
              </div>
            </div>
          </div>

          {/* Mission / 理念 */}
          <div className="border-t pt-6 mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Target className="h-5 w-5 text-[#00A651]" />
              {t('admin.settingsTab.mission')}
            </h3>
            <Textarea
              value={settings.mission || ''}
              onChange={(e) => setSettings({...settings, mission: e.target.value})}
              disabled={!editMode}
              rows={5}
              placeholder={t('admin.settingsTab.missionPlaceholder')}
              className="mt-1"
            />
          </div>

          {/* Logo Upload */}
          <div className="border-t pt-6 mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Image className="h-5 w-5 text-[#00A651]" />
              {t('admin.settingsTab.logo')}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {t('admin.settingsTab.logoDesc')}
            </p>
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0">
                {settings.logoImage ? (
                  <div className="relative group">
                    <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                      <img src={getImageUrl(settings.logoImage)} alt={t('admin.settingsTab.logoAlt')} className="max-w-full max-h-full object-contain" />
                    </div>
                    <Button variant="destructive" size="sm" className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" onClick={deleteLogoImage}>
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                    <div className="text-center text-gray-400">
                      <Image className="h-8 w-8 mx-auto mb-1" />
                      <span className="text-xs">{t('admin.settingsTab.notSet')}</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <input type="file" ref={logoFileRef} onChange={uploadLogoImage} accept="image/*" className="hidden" />
                <Button variant="outline" onClick={() => logoFileRef.current?.click()} disabled={uploadingLogo} className="mb-2">
                  {uploadingLogo ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('admin.settingsTab.uploading')}</>) : (<><Upload className="h-4 w-4 mr-2" />{settings.logoImage ? t('admin.settingsTab.changeLogo') : t('admin.settingsTab.uploadLogo')}</>)}
                </Button>
                <div className="text-xs text-gray-500 space-y-1">
                  <p>{t('admin.settingsTab.logoRecommend')}</p>
                  <p>{t('admin.settingsTab.logoMaxSize')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Bank Transfer Settings */}
          <div className="border-t pt-6 mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Banknote className="h-5 w-5 text-[#00A651]" />
              {t('admin.settingsTab.bankInfo')}
            </h3>
            <p className="text-sm text-gray-500 mb-4">{t('admin.settingsTab.bankInfoDesc')}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>{t('admin.settingsTab.bankName')}</Label>
                <Input value={settings.bankName || ''} onChange={(e) => setSettings({...settings, bankName: e.target.value})} disabled={!editMode} placeholder="例: 三菱UFJ銀行" className="mt-1" />
              </div>
              <div>
                <Label>{t('admin.settingsTab.branchName')}</Label>
                <Input value={settings.bankBranch || ''} onChange={(e) => setSettings({...settings, bankBranch: e.target.value})} disabled={!editMode} placeholder="例: 東京支店" className="mt-1" />
              </div>
              <div>
                <Label>{t('admin.settingsTab.accountType')}</Label>
                <select value={settings.bankAccountType || ''} onChange={(e) => setSettings({...settings, bankAccountType: e.target.value})} disabled={!editMode} className="mt-1 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm">
                  <option value="">{t('admin.settingsTab.selectPlease')}</option>
                  <option value="普通">{t('admin.settingsTab.savings')}</option>
                  <option value="当座">{t('admin.settingsTab.checking')}</option>
                </select>
              </div>
              <div>
                <Label>{t('admin.settingsTab.accountNumber')}</Label>
                <Input value={settings.bankAccountNumber || ''} onChange={(e) => setSettings({...settings, bankAccountNumber: e.target.value})} disabled={!editMode} placeholder="例: 1234567" className="mt-1" />
              </div>
              <div className="md:col-span-2">
                <Label>{t('admin.settingsTab.accountHolder')}</Label>
                <Input value={settings.bankAccountHolder || ''} onChange={(e) => setSettings({...settings, bankAccountHolder: e.target.value})} disabled={!editMode} placeholder="例: ニホンロウドウシャアンゼンエイセイスイシンキョウイクカイ" className="mt-1" />
              </div>
            </div>
          </div>

          {/* Seal Image Upload Section */}
          <div className="border-t pt-6 mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Award className="h-5 w-5 text-[#00A651]" />
              {t('admin.settingsTab.sealImage')}
            </h3>
            <p className="text-sm text-gray-500 mb-4">{t('admin.settingsTab.sealImageDesc')}</p>
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0">
                {settings.sealImage ? (
                  <div className="relative group">
                    <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                      <img src={getImageUrl(settings.sealImage)} alt={t('admin.settingsTab.sealAlt')} className="max-w-full max-h-full object-contain" />
                    </div>
                    <Button variant="destructive" size="sm" className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" onClick={deleteSealImage}>
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                    <div className="text-center text-gray-400">
                      <Award className="h-8 w-8 mx-auto mb-1" />
                      <span className="text-xs">{t('admin.settingsTab.notSet')}</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <input type="file" ref={sealFileRef} onChange={uploadSealImage} accept="image/*" className="hidden" />
                <Button variant="outline" onClick={() => sealFileRef.current?.click()} disabled={uploadingSeal} className="mb-2">
                  {uploadingSeal ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('admin.settingsTab.uploading')}</>) : (<><Upload className="h-4 w-4 mr-2" />{settings.sealImage ? t('admin.settingsTab.changeImage') : t('admin.settingsTab.uploadImage')}</>)}
                </Button>
                <div className="text-xs text-gray-500 space-y-1">
                  <p>{t('admin.settingsTab.sealRecommend')}</p>
                  <p>{t('admin.settingsTab.sealFormats')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Legal Pages */}
          <div className="border-t pt-6 mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('admin.settingsTab.privacyPolicy')}</h3>
            <Textarea value={settings.privacyPolicy || ''} onChange={(e) => setSettings({...settings, privacyPolicy: e.target.value})} disabled={!editMode} rows={6} placeholder={t('admin.settingsTab.privacyPolicyPlaceholder')} className="mt-1" />
          </div>

          <div className="border-t pt-6 mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('admin.settingsTab.commercialLaw')}</h3>
            <Textarea value={settings.commercialLaw || ''} onChange={(e) => setSettings({...settings, commercialLaw: e.target.value})} disabled={!editMode} rows={6} placeholder={t('admin.settingsTab.commercialLawPlaceholder')} className="mt-1" />
          </div>

          <div className="border-t pt-6 mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('admin.settingsTab.sustainability')}</h3>
            <Textarea value={settings.sustainability || ''} onChange={(e) => setSettings({...settings, sustainability: e.target.value})} disabled={!editMode} rows={6} placeholder={t('admin.settingsTab.sustainabilityPlaceholder')} className="mt-1" />
          </div>
        </div>
      )}
    </TabsContent>
  );
};

export default SettingsTab;
